import { getLinks } from '@cinch-nx/data-storyblok'
import { GetServerSidePropsContext } from 'next'
import { getServerSideSitemap } from 'next-sitemap'

import { Rewrite, StartsWith, transform } from './utils/transform'
import { setTag } from '@core/datadog-server'

function filterOnlyFulfilled<T>(input: PromiseSettledResult<T>[]) {
  return input.filter(
    (item): item is PromiseFulfilledResult<T> => item.status === 'fulfilled',
  )
}

function readErrorMessage(error: unknown) {
  let errorMessage = 'Unknown Error'

  if (typeof error === 'string') {
    errorMessage = error
  }

  if (error instanceof Error) {
    errorMessage = error.message
  }

  return errorMessage
}

function doWithRetry<T>(
  fn: () => Promise<T>,
  numberOfAttempts = 3,
  attempt = 0,
): Promise<T> {
  return fn().catch((error) => {
    console.error(`Attempt #${attempt} failed: ${readErrorMessage(error)}`)
    if (attempt < numberOfAttempts) {
      attempt++
      return doWithRetry<T>(fn, numberOfAttempts, attempt)
    }

    throw new Error('Exceeded retry amounts')
  })
}

async function fetchLinks(paths: StartsWith[]) {
  const results = await Promise.allSettled(
    paths.map((url) => doWithRetry(() => getLinks(url, null), 3)),
  )

  if (results.every((res) => res.status === 'rejected')) {
    throw new Error('everything has gone wrong.')
  }

  return filterOnlyFulfilled(results).map((res) => res.value)
}

export function buildGetServerSideProps(
  paths: StartsWith[],
  rewriteRules: Rewrite[],
  exclude: string[],
  extraPaths: string[],
) {
  return async (ctx: GetServerSidePropsContext) => {
    setTag('page.url', ctx.req.url)
    setTag('page.name', 'sitemap')
    setTag('page.rendered', 'SSR')
    setTag(
      'page.params',
      JSON.stringify({ paths, rewriteRules, exclude, extraPaths }, null, 2),
    )

    const links = await fetchLinks(paths)
    const linksToSitemapLinks = transform(exclude, rewriteRules, extraPaths)

    setTag('request.status', 200)
    return getServerSideSitemap(ctx, linksToSitemapLinks(links))
  }
}
